import React, { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { isMobileUa } from '../../../../../shared/features/uatraits';
import { useElementWidth } from '../../../../../shared/hooks/useElementWidth';
import { isLocalRecordingActive, isLocalRecordingAllowed } from '../../../selectors';
import { LocalRecordingButton, LocalRecordingOption } from '../toolbar-controls/local-recording';
import { useRequiredRecordingButtonWidth } from './use-required-recording-button-width';

export interface LimitationPopupOptions {
    isShowLimitationPopup: boolean;
    toggleLimitationPopup: () => void;
}

export interface UseLocalRecordingControlOptions {
    canButtonAllocate?: boolean;
    limitationPopupOptions: LimitationPopupOptions;
}

export function useLocalRecordingControl(availableSpace: number, options: UseLocalRecordingControlOptions) {
    const { canButtonAllocate, limitationPopupOptions } = { canButtonAllocate: true, ...options };

    const isLocalRecordingAvailable = useSelector(isLocalRecordingAllowed);
    const isMobile = useSelector(isMobileUa);
    const isRecording = useSelector(isLocalRecordingActive);

    const [ref, width] = useElementWidth();

    const unwrappedButton = useMemo(() => {
        return <LocalRecordingButton {...limitationPopupOptions} />;
    }, [limitationPopupOptions]);

    const { button, option } = useMemo(() => {
        return {
            button: (
                <div ref={ref} key="localRecording">
                    {unwrappedButton}
                </div>
            ),
            option: <LocalRecordingOption key="localRecording" />,
        };
    }, [ref, unwrappedButton]);

    const requiredWidth = useRequiredRecordingButtonWidth(width);
    const isEnoughSpace = availableSpace > requiredWidth;

    const isAvailable = isLocalRecordingAvailable && !isMobile;
    const isButton = isEnoughSpace && isRecording && canButtonAllocate;

    const localRecordingButton = isAvailable && isButton ? button : null;
    const localRecordingOption = isAvailable && !isButton ? option : null;

    return { localRecordingButton, localRecordingOption };
}
